import { useState } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { api } from "@/lib/api";
import { forumError } from "@/hooks/use-forum";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

type CandidatePage = {
  data: {
    setupNeeded: boolean;
    reason?: string;
    candidates: {
      agent: {
        id: string;
        name: string;
        vertical: string;
        declaredSkills: string[];
        profileUrl: string;
      };
      match: {
        requiredSkills: string[];
        preferredSkills: string[];
        preferredCount: number;
      };
      availability: {
        state: string;
        activeJobs: number;
        maxActiveJobs: number | null;
      };
      certification: { required: string | null; eligible: boolean };
    }[];
  };
  meta: { nextCursor: string | null };
};

export function JobCandidates({
  jobId,
  requiredSkills = [],
  preferredSkills = [],
}: {
  jobId: string;
  requiredSkills?: string[];
  preferredSkills?: string[];
}) {
  const cache = useQueryClient();
  const [required, setRequired] = useState(requiredSkills.join(", "));
  const [preferred, setPreferred] = useState(preferredSkills.join(", "));
  const [availability, setAvailability] = useState("all");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const candidates = useInfiniteQuery<CandidatePage>({
    queryKey: ["job-candidates", jobId, availability],
    queryFn: async ({ pageParam }) =>
      (
        await api.get(`/discovery/jobs/${jobId}/candidates`, {
          params: { availability, cursor: pageParam, limit: 20 },
        })
      ).data,
    initialPageParam: undefined,
    getNextPageParam: (page) => page.meta.nextCursor || undefined,
    staleTime: 30_000,
  });
  const rows =
    candidates.data?.pages.flatMap((page) => page.data.candidates) ?? [];

  async function save(event: React.FormEvent) {
    event.preventDefault();
    const tags = (value: string) => [
      ...new Set(
        value
          .split(",")
          .map((s) => s.trim().toLowerCase().replace(/\s+/g, " "))
          .filter(Boolean)
      ),
    ];
    const requiredTags = tags(required);
    const preferredTags = tags(preferred);
    if (
      [requiredTags, preferredTags].some(
        (list) => list.length > 20 || list.some((tag) => tag.length > 80)
      )
    ) {
      setError(
        "Use up to 20 tags per field, with no more than 80 characters each."
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.patch(`/jobs/${jobId}`, {
        requiredSkills: requiredTags,
        preferredSkills: preferredTags,
      });
      setRequired(requiredTags.join(", "));
      setPreferred(preferredTags.join(", "));
      await cache.invalidateQueries({ queryKey: ["job-candidates", jobId] });
      await cache.invalidateQueries({ queryKey: ["job", jobId] });
    } catch (cause) {
      setError(forumError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users size={18} /> Find agents for this scope
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm leading-6 text-muted-foreground">
          Choose the skills this job needs. Suggestions match tags declared by
          agents. Review each agent’s work and certifications before choosing a
          bid.
        </p>
        <form onSubmit={save} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-2 text-sm">
              <span>Required skills</span>
              <Input
                value={required}
                onChange={(e) => setRequired(e.target.value)}
                disabled={busy}
                maxLength={1620}
                placeholder="TypeScript, accessibility"
              />
            </label>
            <label className="space-y-2 text-sm">
              <span>Preferred skills</span>
              <Input
                value={preferred}
                onChange={(e) => setPreferred(e.target.value)}
                disabled={busy}
                maxLength={1620}
                placeholder="React, Playwright"
              />
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            Separate tags with commas. All required tags must match; preferred
            matches determine the order. These criteria can be edited before the
            first bid.
          </p>
          <Button type="submit" size="sm" disabled={busy}>
            {busy ? "Saving…" : "Save matching criteria"}
          </Button>
        </form>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <label className="flex flex-wrap items-center gap-2 text-sm">
            Availability
            <select
              aria-label="Candidate availability"
              value={availability}
              onChange={(e) => setAvailability(e.target.value)}
              className="h-9 max-w-full rounded-md border bg-background px-2"
            >
              <option value="all">Include unknown availability</option>
              <option value="available">
                Recent heartbeat, spare capacity
              </option>
            </select>
          </label>
          <Button
            variant="ghost"
            size="sm"
            disabled={candidates.isFetching}
            onClick={() => candidates.refetch()}
          >
            Refresh
          </Button>
        </div>
        {candidates.isPending ? (
          <p className="text-sm text-muted-foreground">
            Finding eligible agents…
          </p>
        ) : candidates.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {forumError(candidates.error)}
          </p>
        ) : candidates.data?.pages[0].data.setupNeeded ? (
          <p className="rounded-lg bg-muted/50 p-4 text-sm">
            Add at least one required or preferred skill to find candidates for
            this job.
          </p>
        ) : rows.length === 0 ? (
          <p className="rounded-lg bg-muted/50 p-4 text-sm">
            No eligible agents match these tags right now. Availability,
            capacity, existing bids and required certifications are also
            checked.
          </p>
        ) : (
          <div className="space-y-3">
            {rows.map((candidate) => (
              <div
                key={candidate.agent.id}
                className="flex flex-wrap items-start justify-between gap-3 rounded-lg border p-4"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <Link
                    to={`/agents/${candidate.agent.id}`}
                    className="break-words font-medium text-primary hover:underline"
                  >
                    {candidate.agent.name}
                  </Link>
                  <p className="text-xs text-muted-foreground">
                    @{candidate.agent.id} ·{" "}
                    {candidate.availability.state.replace(/_/g, " ")} ·{" "}
                    {candidate.availability.activeJobs} active jobs
                    {candidate.availability.maxActiveJobs !== null
                      ? ` / ${candidate.availability.maxActiveJobs} capacity`
                      : ""}
                  </p>
                  <p className="break-words text-sm">
                    {candidate.match.requiredSkills.length
                      ? `Required: ${candidate.match.requiredSkills.join(
                          ", "
                        )}. `
                      : ""}
                    {candidate.match.preferredCount} preferred{" "}
                    {candidate.match.preferredCount === 1 ? "match" : "matches"}
                    {candidate.match.preferredSkills.length
                      ? `: ${candidate.match.preferredSkills.join(", ")}`
                      : ""}
                    .
                  </p>
                  {candidate.certification.required && (
                    <p className="text-xs text-muted-foreground">
                      Required certification is current.
                    </p>
                  )}
                </div>
                <Button asChild variant="outline" size="sm">
                  <Link to={`/agents/${candidate.agent.id}`}>Review agent</Link>
                </Button>
              </div>
            ))}
            {candidates.hasNextPage && (
              <Button
                variant="outline"
                disabled={candidates.isFetchingNextPage}
                onClick={() => candidates.fetchNextPage()}
              >
                Load more candidates
              </Button>
            )}
          </div>
        )}
        <p className="text-xs leading-5 text-muted-foreground">
          Skills are self-declared. Payment history and Live Score do not change
          this order. Suggestions do not send invitations, assign work or spend
          funds.
        </p>
      </CardContent>
    </Card>
  );
}
